import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { Employee } from "@/types/global";
import { EMPTY_VALUE_FILTER_OPERATOR } from "@/utils/constant/global.constant";

interface Filter {
  field: { label: string; value: string };
  operator: string;
  value: string[];
}
interface VIEW {
  isNoteEditorOpen: boolean;
  refresh: string;
  note: any;
}

interface SortBy {
  field: string;
  isASC: boolean;
}

interface Pagination {
  pageSize: number;
  pageNumber: number;
}

interface EmployeeState {
  filters: Filter[];
  fetchFilters: Filter[];
  sortBy: SortBy[];
  pagination: Pagination;
  searchQuery: string;
  groupBy?: string;
  employeesData: Employee[] | null;
  view: VIEW;
  employeeInfo: any;
  tableConfig?: {
    visibleFields: string[];
    hiddenFields: string[];
  } | null;
  pinned: string[];
}

const initialState: EmployeeState = {
  filters: [],
  fetchFilters: [],
  sortBy: [],
  pagination: { pageSize: 25, pageNumber: 1 },
  searchQuery: "",
  groupBy: "",
  employeesData: null,
  view: { isNoteEditorOpen: false, refresh: "", note: null },
  employeeInfo: null,
  tableConfig: null,
  pinned: [],
};

const EmployeeSlice = createSlice({
  name: "employees",
  initialState,
  reducers: {
    addFilter: (state) => {
      const defaultFilter: Filter = {
        field: { label: "", value: "" },
        operator: "",
        value: [],
      };
      const hasEmptyValue = state.filters.some(
        (filter: any) => (!filter.value || filter.value.length === 0) && !EMPTY_VALUE_FILTER_OPERATOR.includes(filter?.operator?.value)
      );
      if (!hasEmptyValue) {
        state.filters.unshift(defaultFilter);
      }
    },
    updateFilter: (state, action: PayloadAction<{ index: number; key: keyof Filter; value: any }>) => {
      const { index, key, value } = action.payload;
      if (state.filters[index]) {
        state.filters[index][key] = value;
      }
    },
    removeFilter: (state, action: PayloadAction<number>) => {
      state.filters.splice(action.payload, 1);
      if (state.filters.length === 0) {
        state.fetchFilters = [];
      }
    },
    resetFilters: (state) => {
      state.filters = [];
      state.fetchFilters = [];
    },
    setFetchFilter: (state) => {
      state.fetchFilters = state.filters.map(({ field, operator, value }) => ({
        field,
        operator: (operator as any)?.value ?? operator,
        value,
      }));
    },
    setFilter: (state, action: PayloadAction<any>) => {
      state.filters = action.payload;
    },

    setEmployeeData: (state, action: PayloadAction<Employee[] | null>) => {
      state.employeesData = action.payload;
    },
    setNoteEditor: (state, action: PayloadAction<boolean>) => {
      state.view.isNoteEditorOpen = action.payload;
      if (!action.payload) {
        state.view.note = null;
        state.view.refresh = "activity";
      } else {
        state.view.refresh = "";
      }
    },
    setEmployeeInfo: (state, action: PayloadAction<any>) => {
      state.employeeInfo = action.payload;
    },
    setNoteUpdate: (state, action: PayloadAction<any>) => {
      state.view.isNoteEditorOpen = true;
      state.view.note = action.payload;
    },
    resetNote: (state) => {
      state.view.isNoteEditorOpen = false;
      state.view.note = null;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<SortBy[]>) => {
      state.sortBy = action.payload;
    },
    setPagination: (state, action: PayloadAction<Pagination>) => {
      state.pagination = action.payload;
    },
    setGroupBy: (state, action: PayloadAction<string>) => {
      state.groupBy = action.payload;
    },
    setTableConfig: (state, action: PayloadAction<any>) => {
      state.tableConfig = action.payload;
    },
    setPinned: (state, action: PayloadAction<string[]>) => {
      state.pinned = action.payload;
    },
  },
});

export const {
  addFilter,
  updateFilter,
  removeFilter,
  resetFilters,
  setEmployeeData,
  setNoteEditor,
  setSearchQuery,
  setSortBy,
  setPagination,
  setGroupBy,
  setFetchFilter,
  setNoteUpdate,
  resetNote,
  setEmployeeInfo,
  setTableConfig,
  setPinned,
  setFilter,
} = EmployeeSlice.actions;
export const employeeSelector = (state: RootState) => state.employees;

export default EmployeeSlice.reducer;
